import { Group, Stack, Stepper } from '@mantine/core'
import type {
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
} from '@medplum/fhirtypes'
import { QuestionnaireItemType } from '@medplum/react-hooks'
import { Button } from '@seen/ui'
import type { JSX } from 'react'
import { QuestionnaireRepeatableItem } from '#src/components/questionnaire-form/QuestionnaireRepeatableItem.tsx'
import { QuestionnaireRepeatedGroup } from '#src/components/questionnaire-form/QuestionnaireRepeatedGroup.tsx'

interface QuestionnairePageSequenceProps {
  readonly items: QuestionnaireItem[]
  readonly response?: QuestionnaireResponse
  readonly renderPages: boolean
  readonly activePage?: number
  readonly numberOfPages: number
  readonly submitting?: boolean
  readonly checkForQuestionEnabled: (item: QuestionnaireItem) => boolean
  readonly onChange: (
    items: QuestionnaireResponseItem | QuestionnaireResponseItem[]
  ) => void
  readonly nextStep: () => void
  readonly prevStep: () => void

  // =============================
  // PATCH
  // Adding a new prop to hide the submit button
  // =============================
  readonly hideSubmitButton?: boolean
}

export function QuestionnairePageSequence(
  props: QuestionnairePageSequenceProps
): JSX.Element {
  const {
    items,
    response,
    activePage,
    onChange,
    nextStep,
    prevStep,
    numberOfPages,
    renderPages,
    submitting,
    checkForQuestionEnabled,
    hideSubmitButton,
  } = props

  const form = items.map((item) => {
    const itemResponse =
      response?.item?.filter((i) => i.linkId === item.linkId) ?? []

    const repeatedItem =
      item.type === QuestionnaireItemType.group ? (
        <QuestionnaireRepeatedGroup
          key={item.linkId}
          item={item}
          response={itemResponse}
          onChange={onChange}
          checkForQuestionEnabled={checkForQuestionEnabled}
        />
      ) : (
        <QuestionnaireRepeatableItem
          key={item.linkId}
          item={item}
          response={itemResponse?.[0]}
          onChange={onChange}
          checkForQuestionEnabled={checkForQuestionEnabled}
        />
      )

    if (renderPages) {
      return (
        <Stepper.Step key={item.linkId} label={item.text}>
          {repeatedItem}
        </Stepper.Step>
      )
    }
    return repeatedItem
  })

  return (
    <>
      {renderPages && (
        <Stepper active={activePage ?? 0} allowNextStepsSelect={false} p={6}>
          {form}
        </Stepper>
      )}
      {!renderPages && <Stack>{form}</Stack>}
      <ButtonGroup
        activePage={activePage ?? 0}
        numberOfPages={numberOfPages}
        nextStep={nextStep}
        prevStep={prevStep}
        submitting={submitting}
        hideSubmitButton={hideSubmitButton}
      />
    </>
  )
}

interface ButtonGroupProps {
  readonly activePage: number
  readonly numberOfPages: number
  readonly submitting?: boolean
  readonly nextStep: () => void
  readonly prevStep: () => void
  readonly hideSubmitButton?: boolean
}

function ButtonGroup(props: ButtonGroupProps): JSX.Element {
  const showBackButton = props.activePage > 0
  const showNextButton = props.activePage < props.numberOfPages - 1
  const showSubmitButton =
    props.activePage === props.numberOfPages - 1 &&
    props.hideSubmitButton !== true

  return (
    <Group justify="flex-end" mt="xl" gap="xs">
      {showBackButton && (
        <Button disabled={props.submitting} onClick={props.prevStep}>
          Back
        </Button>
      )}
      {showNextButton && (
        <Button
          disabled={props.submitting}
          onClick={(e) => {
            const form = e.currentTarget.closest('form') as HTMLFormElement
            if (form.reportValidity()) {
              props.nextStep()
            }
          }}
        >
          Next
        </Button>
      )}
      {showSubmitButton && (
        <Button loading={props.submitting} type="submit">
          Submit
        </Button>
      )}
    </Group>
  )
}
